import React, { useEffect, useState } from "react";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import OptionPicker from "components/OptionPicker";
import {
  EMPTY_OPTION,
  IOptionPickerProps
} from "components/OptionPicker/OptionPicker";
import { FirstApiError } from "hooks/api/errors";
import { useRacehorse360Api } from "hooks/api";
import { stallNumberPickerOptions } from "./options";

type StallNumberPickerBaseProps = Omit<
  IOptionPickerProps,
  "options" | "optionId" | "onConfirm"
>;

interface IProps extends StallNumberPickerBaseProps {
  onStallChange?: (patch: racehorse360.IHorse, horseId?: string) => void;
}

const StallNumberPicker = (props: IProps) => {
  const { horse, onStallChange, ...restProps } = props;

  const [stallId, setStallId] = useState(horse.stallNumber || EMPTY_OPTION.id);

  const { useAssignHorseToStall, useUnassignHorseFromStall } =
    useRacehorse360Api();

  const { mutateAsync: assignHorseToStall, isPending: isHorseAssigning } =
    useAssignHorseToStall();
  const { mutateAsync: unAssignHorseFromStall, isPending: isHorseUnAssigning } =
    useUnassignHorseFromStall();

  const isLoading = [isHorseAssigning, isHorseUnAssigning].some(Boolean);

  const handleConfirm = async (value: string) => {
    const query: Partial<racehorse360.AssignHorseToStallRequest> = {
      horseId: horse?.id,
      stallNumber: null,
      getOptions: {
        select: ["stallNumber"]
      }
    };

    let changeBarnRequest: UseMutateAsyncFunction<
      Partial<Omit<racehorse360.IHorse, "toJSON">>,
      FirstApiError,
      | racehorse360.IUnassignHorseFromBarnRequest
      | racehorse360.IAssignHorseToBarnRequest
    >;

    if (value === EMPTY_OPTION.id) {
      changeBarnRequest = unAssignHorseFromStall;
    } else {
      changeBarnRequest = assignHorseToStall;
      query.stallNumber = value;
    }

    return changeBarnRequest(query).then(horse => {
      setStallId(horse.stallNumber || EMPTY_OPTION.id);
      onStallChange?.({ stallNumber: horse.stallNumber }, horse.id);
    });
  };

  useEffect(() => {
    setStallId(horse.stallNumber || EMPTY_OPTION.id);
  }, [horse.stallNumber]);

  return (
    <OptionPicker
      horse={horse}
      options={stallNumberPickerOptions}
      onConfirm={handleConfirm}
      optionId={stallId}
      isLoading={isLoading}
      isEditable={Boolean(horse.barn)}
      {...restProps}
    />
  );
};

export default React.memo(StallNumberPicker);
